import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  memo,
} from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import useStyles from '../styles';
import SelectInvestmentAmount from '../common/SelectAmount';
import FundAllocationCard from '../common/FundAllocationCard';
import RemainingAllocationDisplay from '../common/RemainingAllocationDisplay';
import { PlanContext } from '../../context/PlanContext';
import { FundType } from '../../constants';
import isInvestmentAmountValid from '../../helpers/investmentAmount';

const errorMessage = 'Your Sub-Allocation and Total Allocation does not match. Please adjust one or the other to proceed.';

export const ErrorTypography = styled(Typography)({
  fontSize: 16,
  fontWeight: 600,
  '&&': {
    color: '#AB2B28',
  },
});

const AllocateBudget = ({ setNextDisabled }) => {
  const classes = useStyles();
  const {
    plan,
    updatedPlan,
    setUpdatedPlan,
    userPortfolio,
    planningConfig,
    loading,
  } = useContext(PlanContext);
  const isFormDataInitialized = useRef(false);

  const [formData, setFormData] = useState({
    coreFundAllocation: '',
    focusedFundAllocation: '',
    followOnFundAllocation: '',
    syndicationFundAllocation: '',
    amount: 0,
  });

  const getAllocation = (fundType) => {
    const allocation = plan?.fundTypeAllocations?.find(
      (candidateAllocation) => candidateAllocation.fundType === fundType,
    );
    return allocation?.amount && parseFloat(allocation.amount) > 0
      ? parseFloat(allocation.amount)
      : '';
  };

  const totalAmount = [
    getAllocation(FundType.CORE),
    getAllocation(FundType.FOCUSED),
    getAllocation(FundType.FOLLOW_ON),
    getAllocation(FundType.SYNDICATION),
  ].reduce((total, allocation) => total + (parseFloat(allocation) || 0), 0);

  const remainingAllocation = (updatedFormData = formData) => (
    parseFloat(updatedFormData.amount || 0)
      - (parseFloat(updatedFormData.coreFundAllocation || 0)
        + parseFloat(updatedFormData.focusedFundAllocation || 0)
        + parseFloat(updatedFormData.followOnFundAllocation || 0)
        + parseFloat(updatedFormData.syndicationFundAllocation || 0))
  );

  const validateNextDisabled = (updatedFormData) => {
    setNextDisabled(
      remainingAllocation(updatedFormData) !== 0
      || !isInvestmentAmountValid(
        updatedFormData.amount,
        planningConfig?.budgetAllocation?.selectInvestmentAmount,
      ),
    );
  };

  const updateFormData = () => {
    const updatedForm = {
      coreFundAllocation: getAllocation(FundType.CORE),
      focusedFundAllocation: getAllocation(FundType.FOCUSED),
      followOnFundAllocation: getAllocation(FundType.FOLLOW_ON),
      syndicationFundAllocation: getAllocation(FundType.SYNDICATION),
      amount: totalAmount,
    };
    setFormData(updatedForm);
    validateNextDisabled(updatedForm);
  };

  useEffect(() => {
    if (!loading && Object.keys(plan).length) {
      if (!isFormDataInitialized.current) {
        updateFormData();
      }
      isFormDataInitialized.current = true;
    } else {
      setNextDisabled(true);
    }
  }, [loading]);

  const buildAllocation = (fundType, amount) => {
    const normalizedAmount = Number(amount || 0);
    const allocation = { fundType, amount: String(normalizedAmount) };
    if (plan?.id) {
      allocation.fkInvestmentPlanId = plan.id;
    }
    const existingAllocation = plan?.fundTypeAllocations?.find(
      (candidateAllocation) => candidateAllocation.fundType === fundType,
    );
    if (existingAllocation?.id) { allocation.id = existingAllocation.id; }
    if (existingAllocation?.fkInvestmentPlanId) { allocation.fkInvestmentPlanId = existingAllocation.fkInvestmentPlanId; }
    if (normalizedAmount === 0) {
      allocation.perFundAllocations = [];
    }
    return allocation;
  };

  const updatePlan = (updatedFormData) => {
    const updatedAllocationPlan = {
      ...updatedPlan,
      userId: parseInt(userPortfolio.integrationId, 10),
      fundTypeAllocations: [
        buildAllocation(FundType.CORE, updatedFormData.coreFundAllocation),
        buildAllocation(
          FundType.FOCUSED,
          updatedFormData.focusedFundAllocation,
        ),
        buildAllocation(
          FundType.FOLLOW_ON,
          updatedFormData.followOnFundAllocation,
        ),
        buildAllocation(
          FundType.SYNDICATION,
          updatedFormData.syndicationFundAllocation,
        ),
      ],
    };
    if (!updatedAllocationPlan.id) {
      updatedAllocationPlan.createNewPlan = true;
    }
    setUpdatedPlan(updatedAllocationPlan);
  };

  const handleUpdateAllocation = useCallback(
    (updatedFormData) => {
      if (!loading) {
        updatePlan(updatedFormData);
        validateNextDisabled(updatedFormData);
      }
    },
    [formData],
  );

  if (loading || !planningConfig?.budgetAllocation) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Box className={classes.content}>
      <Typography
        variant="h1ExtraLarge"
        component="h1"
        style={{ color: '#191919' }}
      >
        {planningConfig?.budgetAllocation?.title}
      </Typography>
      <Typography
        variant="body2Inter"
        style={{ color: '#797979', padding: 15 }}
        dangerouslySetInnerHTML={{
          __html: planningConfig?.budgetAllocation?.subtitle?.richtext || '',
        }}
      />
      <Grid container xs={12} sm={10}>
        <SelectInvestmentAmount
          selectInvestmentAmount={
            planningConfig?.budgetAllocation?.selectInvestmentAmount
          }
          formData={formData}
          setFormData={setFormData}
          onUpdateAllocation={handleUpdateAllocation}
        />
        <FundAllocationCard
          title={planningConfig?.budgetAllocation?.coreFundTitle}
          subtitle={
            planningConfig?.budgetAllocation?.coreFundBodyCopy?.richtext
          }
          formData={formData}
          setFormData={setFormData}
          field="coreFundAllocation"
          onUpdateAllocation={handleUpdateAllocation}
        />
        <FundAllocationCard
          title={planningConfig?.budgetAllocation?.focusedFundTitle}
          subtitle={
            planningConfig?.budgetAllocation?.focusedFundBodyCopy?.richtext
          }
          formData={formData}
          setFormData={setFormData}
          field="focusedFundAllocation"
          onUpdateAllocation={handleUpdateAllocation}
        />
        <FundAllocationCard
          title={planningConfig?.budgetAllocation?.followOnFundTitle}
          subtitle={
            planningConfig?.budgetAllocation?.followOnFundBodyCopy?.richtext
          }
          formData={formData}
          setFormData={setFormData}
          field="followOnFundAllocation"
          onUpdateAllocation={handleUpdateAllocation}
        />
        <FundAllocationCard
          title={planningConfig?.budgetAllocation?.syndicationFundTitle}
          subtitle={
            planningConfig?.budgetAllocation?.syndicationFundBodyCopy?.richtext
          }
          formData={formData}
          setFormData={setFormData}
          field="syndicationFundAllocation"
          onUpdateAllocation={handleUpdateAllocation}
        />
        <Box display="flex" justifyContent="center" width="100%">
          <RemainingAllocationDisplay
            remainingAllocation={remainingAllocation()}
            selectedTotal={formData.amount}
          />
        </Box>
      </Grid>
      {remainingAllocation() < 0 && (
        <ErrorTypography align="center" style={{ marginTop: '16px' }}>
          {errorMessage}
        </ErrorTypography>
      )}
      {planningConfig?.budgetAllocation?.selectInvestmentAmount?.diamondClubBox
        ?.footnoteCopy?.richtext && (
        <Typography
          variant="disclaimer"
          component="p"
          sx={{
            margin: { xs: '20px 20px', lg: '20px 250px' },
            fontSize: '12px',
          }}
          dangerouslySetInnerHTML={{
            __html:
              planningConfig?.budgetAllocation?.selectInvestmentAmount
                ?.diamondClubBox?.footnoteCopy?.richtext || '',
          }}
        />
      )}
    </Box>
  );
};

AllocateBudget.propTypes = {
  setNextDisabled: PropTypes.func.isRequired,
};

export default memo(AllocateBudget);
